import { FC, useState } from 'react';

import { useMeetTools } from '../../../features/meet/MeetToolsProvider';
import { useAppDispatch } from '../../../hooks/redux';
import { clearMacClientSelections, IMacCardInfo } from '../../../store/mac.slice';

import ModalWrap from '../../common/modal-wrap';
import Button from '../../common/button';
import { CloseCrossBtn } from '../../icons/close-cross-btn';

import { MacDeckSelectContent } from './mac-deck-select-content';
import { MacGroupPsychologistResults } from './mac-group-psychologist-results';

const MAC_CONFIRM_CLOSE = {
  name: '«МАК карты»',
  description: 'Клиент перестанет видеть карты.',
};

export interface MacGroupPanelProps {
  onClose: () => void;
  onConfirm?: () => void;
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  /** Закрытие МАК у клиентов по data channel LiveKit (и socket для совместимости) */
  sendMacCardsClose?: () => void;
  /** Identity участников на встрече (кроме психолога) — для экрана «Клиенты делают выбор» и табов */
  expectedParticipantIds: string[];
  /** Имена участников для табов (identity -> displayName) */
  participantDisplayNames?: Record<string, string>;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба — для трансляции карт на «общий экран» клиентам */
  onActiveTabChange?: (participantLabel: string, cards: IMacCardInfo[]) => void;
}

/**
 * Нижняя панель МАК для психолога в групповой терапии.
 * Фазы: выбор колоды → «Клиенты делают выбор» → табы с результатами по участникам.
 * При закрытии крестиком показывается модалка подтверждения, как у остальных инструментов.
 */
export const MacGroupPanel: FC<MacGroupPanelProps> = ({
  onClose,
  onConfirm,
  sendMacCardsOpen,
  sendMacCardsClose,
  expectedParticipantIds,
  participantDisplayNames,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const dispatch = useAppDispatch();
  const tools = useMeetTools();
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const handleConfirm = () => {
    /**
     * Снимок участников храним в провайдере встречи, чтобы состояние
     * не сбрасывалось при временной перестройке layout (например, screen share).
     */
    tools.setMacGroupExpectedParticipantIds([...expectedParticipantIds]);
    tools.setMacGroupParticipantDisplayNames({ ...participantDisplayNames });
    dispatch(clearMacClientSelections());
    tools.setMacGroupDeckShown(true);
    onConfirm?.();
  };

  const handleRequestClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(false);
    sendMacCardsClose?.();
    onClose();
  };

  const handleCancelClose = () => {
    setIsConfirmCloseOpen(false);
  };

  const confirmMessage = `Вы действительно хотите закрыть инструмент ${MAC_CONFIRM_CLOSE.name}? ${MAC_CONFIRM_CLOSE.description}`;

  const confirmCloseModal = (
    <ModalWrap isShow={isConfirmCloseOpen}>
      <div className="think-scroll flex flex-col gap-6 w-full max-w-[90%] sm:max-w-[60%] md:max-w-[560px] h-auto rounded-[20px] p-6 lg:p-10 bg-white shadow-shadowPopup">
        <div className="w-full flex justify-between items-start">
          <h2 className="font-comfortaa text-lg lg:text-2xl text-[#111026] font-bold">Закрытие инструмента</h2>
          <button className="cursor-pointer" onClick={handleCancelClose} aria-label="Отменить закрытие">
            <CloseCrossBtn width={20} height={20} />
          </button>
        </div>
        <p className="text-sm md:text-base font-bold text-[#585858]">{confirmMessage}</p>
        <div className="flex justify-end gap-4">
          <Button type="grey" className="!w-max px-4 py-2 !text-sm md:!text-base" onClick={handleCancelClose}>
            Оставить открытым
          </Button>
          <Button type="black" className="!w-max px-4 py-2 !text-sm md:!text-base" onClick={handleConfirmClose}>
            Закрыть инструмент
          </Button>
        </div>
      </div>
    </ModalWrap>
  );

  if (tools.macGroupDeckShown) {
    return (
      <>
        <MacGroupPsychologistResults
          expectedParticipantIds={tools.macGroupExpectedParticipantIds}
          participantDisplayNames={tools.macGroupParticipantDisplayNames}
          onClose={handleRequestClose}
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
          onActiveTabChange={onActiveTabChange}
        />
        {confirmCloseModal}
      </>
    );
  }

  return (
    <>
      <div className="h-full w-full bg-white rounded-[5px] md:rounded-[15px] overflow-hidden">
        <MacDeckSelectContent
          onClose={handleRequestClose}
          onConfirm={handleConfirm}
          sendMacCardsOpen={sendMacCardsOpen}
          variant="panel"
          panelLightTheme
          className="bg-white text-[#111026]"
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
        />
      </div>
      {confirmCloseModal}
    </>
  );
};
